import { Grid } from '@mui/material';
import { RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';

import { getFileNameFromPath } from '@nx-smartmonkey/shared/helpers';
import { exportToExcel, getStopExamples } from '../../../../../../assets/templates/examples';
import { useGetMapDefaults } from '../../../../../../hooks/map/useGetMapDefaults';
import { useRetrieveActiveProject } from '../../../../../../hooks/project/useRetrieveActiveProject';
import Button from '../../../../../../react-components/Button';
import Text from '../../../../../../react-components/Text';
import { StyledDropzoneContainer, StyledDropzoneContent } from './DropzoneInput';
import { SheetSelector } from './SheetSelector';

interface UploadFileProps {
  onDataLoaded: (data: any) => void;
  onSelectOldVersion: (e: any) => void;
}

export const UploadFile = ({ onDataLoaded, onSelectOldVersion }: UploadFileProps) => {
  const { getCoordinates } = useGetMapDefaults();
  const { data: activeProject } = useRetrieveActiveProject();

  const [stage, setStage] = useState<`initial` | `uploading_file` | `uploading_file_select_sheet` | `parse_data`>(
    `initial`
  );

  const [fileName, setFileName] = useState<string>(``);
  const [workbook, setWorkbook] = useState<any>(); // [ { sheetName: ``, data: [] }

  const handleParseFile = useCallback(
    (workbook: XLSX.WorkBook, sheetName: string) => {
      const worksheet = workbook.Sheets[sheetName];
      try {
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: `` });
        const arrayData = [Object.keys(jsonData[0] as any), ...jsonData.map((row: any) => Object.values(row))];
        onDataLoaded(arrayData);
      } catch (err) {
        console.error(err);
      }
      setStage(`parse_data`);
    },
    [onDataLoaded]
  );

  const handleFileOpen = useCallback(
    async (file: File) => {
      setStage(`uploading_file`);

      if (file) {
        console.info(`file.name`, file.name);

        setFileName(getFileNameFromPath(file.name));

        const reader = new FileReader();
        reader.onload = (e) => {
          if (!e.target) return;
          const data = e.target.result;

          const workbook = XLSX.read(data, { type: `binary` });

          setWorkbook(workbook);

          if (workbook.SheetNames.length > 1) {
            setStage(`uploading_file_select_sheet`);
            return;
          } else {
            handleParseFile(workbook, workbook.SheetNames[0]);
          }
        };
        reader.readAsBinaryString(file);
      }
    },
    [handleParseFile]
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.length > 0 && handleFileOpen(acceptedFiles[0]);
    },
    [handleFileOpen]
  );

  const { getRootProps, getInputProps, open, isDragAccept, isDragReject } = useDropzone({
    multiple: false,
    accept: {
      'text/*': [`.csv`],
      'application/vnd.ms-excel': [`.xls`],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [`.xlsx`],
    },
    onDrop,
  });

  return (
    <Grid style={{ width: `100%` }}>
      {/* WARNING! "onFocus={undefined}" is needed. If its removed, the contained buttons behaves weird: the onClick is not triggered for the first time. */}
      <StyledDropzoneContainer {...getRootProps()} style={{ height: `calc(100% - 24px)` }} onFocus={undefined}>
        {/* This input is required by the library */}
        <input {...getInputProps()} />
        <StyledDropzoneContent
          container
          direction="column"
          justifyContent="center"
          alignItems="center"
          isDragReject={isDragReject}
          isDragAccept={isDragAccept}
        >
          <Grid container direction="column" alignItems="center">
            <Text variant="h5" style={{ cursor: `inherit` }} intlId="plans.dialogs.upload_stops.load_file.title" />

            <VSpacer small />

            <Text
              id="upload-stops-help-article"
              variant="body-large"
              style={{ cursor: `inherit` }}
              intlId="plans.dialogs.upload_stops.load_file.subtitle"
            />

            <VSpacer large />

            <Button
              style={{ width: `150px` }}
              onClick={() => {
                console.info(`NO VA`);
                open();
              }}
              intlId="plans.dialogs.upload_stops.load_file"
            />

            <VSpacer small />

            <Text variant="h6" style={{ color: RoutalPalette.grey7, cursor: `inherit` }} intlId="or" />

            <VSpacer small />

            <Grid>
              <Button
                variant="text"
                styleType="secondary"
                intlId="plans.dialogs.upload_stops.load_file.download_sample_file"
                style={{ width: `150px` }}
                onClick={() => {
                  const coordinates = getCoordinates();
                  if (activeProject && coordinates) {
                    exportToExcel(getStopExamples(coordinates, activeProject), `stops_example`);
                  }
                }}
              />
            </Grid>
          </Grid>

          {stage === `uploading_file_select_sheet` && (
            <SheetSelector
              open={true}
              fileName={fileName}
              sheets={workbook?.SheetNames}
              onDialogClose={(sheetName?: string) => {
                if (sheetName) {
                  handleParseFile(workbook, sheetName);
                } else {
                  setStage(`uploading_file`);
                }
              }}
            />
          )}
        </StyledDropzoneContent>
      </StyledDropzoneContainer>

      <Grid container direction="column" justifyContent="center" alignItems="center" style={{ height: `40px` }}>
        <Text variant="button" intlId="plans.dialogs.upload_stops.use_old_version" onClick={onSelectOldVersion} />
      </Grid>
    </Grid>
  );
};
